/**
 * Use the promotional rail to feature 5+ promoted stories that are related. For example, Beauty steals during a major event. Use when a higher prominence and/or creative image is needed.
 *
 * @module views/components/PromotionalRail
 * @memberof -Common
 */
import './PromotionalRail.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

import Article from '../Article/Article';

/**
 * Represents a PromotionalRail component
 *
 * @method
 * @param {PromotionalRailProps} props - React properties passed from composition
 * @returns PromotionalRail
 */
export const PromotionalRail = function( props ){
  const promotionalRailRef = useRef( null );

  const {
    /* Cards */
    articles,
    backgroundColor,
    dataCapture,
    patternBackground,

    /* Rail Title */
    title = defaultProps.title,
    linkAction,

    /* Rail meta data */
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction
  } = props;

  /* Setting variables */
  let articleCards = articles || [];

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const railMeta = {
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  if( !hasItems( articleCards ) ){
    return null;
  }

  return (
    <div className={
      classNames( 'PromotionalRail', {
        'PromotionalRail__withBackgroundColor': backgroundColor
      } )
    }
    ref={ promotionalRailRef }
    >
      <GridContainer
        {
          ...spreadProps( { fullBleedContentBackgroundColor: !patternBackground ? backgroundColor : undefined } )
        }
      >
        <Rail
          railRef={ promotionalRailRef }
          id={ railUuid }
          headline={ title }
          headlineTag='h2'
          viewAllAction={ linkAction }
          items={ articleCards }
          dataCapture={ dataCapture }
          _railADAmeta={ railADAmeta }
          _railMeta={ railMeta }
          nextClickAction={ nextClickAction }
          previousClickAction={ previousClickAction }
          sliderPerViewXL={ 4 }
          slidesPerGroupXL={ 4 }
          sliderPerViewLG={ 4 }
          slidesPerGroupLG={ 4 }
          sliderPerViewMD={ 3 }
          slidesPerGroupMD={ 3 }
          sliderPerViewSM={ 1.6 }
          slidesPerGroupSM={ 2 }
        >
          { articles?.length > 0 && articles.map( ( article, articleIndex )=>(
            <Slide key={ `PromotionalRail${ articleIndex + 1 } ` }>
              <div role='group'>
                <Article
                  { ...spreadProps( { ...article } ) }
                  key={ articleIndex }
                  backgroundColor={ backgroundColor }
                  promotionalCardAccessibilityKey={ articleIndex }
                />
              </div>
            </Slide>
          ) ) }
        </Rail>
      </GridContainer>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {PromotionalRailProps}
 */
export const defaultProps = {
  previousAccessibility: '',
  nextAccessibility: '',
  title: ''
};
/**
 * Property type definitions
 *
 * @typedef PromotionalRailProps
 * @type {object}
 * @property {array} articles - Sets the Item array in articles.
 * @property {string} backgroundColor - Sets the backgroundColor.
 * @property {string} carouselAccessibility - Sets the value for aria label for carousel.
 * @property {string} carouselDetailsAccessibility - Sets the value for sr-only description.
 * @property {object} linkAction - Sets the linkAction.
 * @property {string} nextAccessibility - Sets the aria label for Next button.
 * @property {object} nextClickAction - This is the action object for the Next button.
 * @property {string} previousAccessibility - Sets the aria label for Previous button.
 * @property {object} previousClickAction - This is the action object for the Previous button.
 * @property {string} title - Sets the headline title.
 *
 */
export const propTypes =  {
  /**
   * Sets the Item array in articles.
   */
  articles: PropTypes.array,
  /**
   * Sets the backgroundColor
   */
  backgroundColor: PropTypes.string,
  /**
   * Sets the value for aria label for carousel
   */
  carouselAccessibility: PropTypes.string,
  /**
   * Sets the value for sr-only description.
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * Sets the linkAction
   */
  linkAction: PropTypes.object,
  /**
   * Sets the aria label for Next button
   */
  nextAccessibility: PropTypes.string,
  /**
   * This is the action object for the Next button.
   */
  nextClickAction: PropTypes.object,
  /**
   * Sets the aria label for Previous button
   */
  previousAccessibility: PropTypes.string,
  /**
   * This is the action object for the Previous button
   */
  previousClickAction: PropTypes.object,
  /**
   * Sets the headline title
   */
  title: PropTypes.string
};

PromotionalRail.propTypes = propTypes;
PromotionalRail.defaultProps = defaultProps;

export default PromotionalRail;
